import React, { createContext, useState } from 'react';
import api from '../api';
import { getPendingPost, clearPendingPost } from '../utils/pendingPostService';
import { getPendingTrip, clearPendingTrip } from '../utils/pendingTripService';
import { companionsApi } from '../pages/companions/shared/companionsApi';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('access'));
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));

    const login = async (email, password) => {
        try {
            const response = await api.post('/auth/jwt/create/', {
                email,
                password,
            });

            localStorage.setItem('access', response.data.access);
            localStorage.setItem('refresh', response.data.refresh);

            const userResponse = await api.get('/auth/users/me/', {
                headers: { Authorization: `Bearer ${response.data.access}` },
            });
            localStorage.setItem('user', JSON.stringify(userResponse.data));

            setUser(userResponse.data);
            setIsAuthenticated(true);
            return { ok: true };
        } catch (error) {
            console.error("Ошибка входа:", error);
            const message = error.response?.data?.detail
                || (typeof error.response?.data === 'object' && error.response?.data && Object.values(error.response.data).flat().find(Boolean))
                || error.message
                || 'Ошибка входа. Проверьте учётные данные.';
            return { ok: false, message };
        }
    };

    const logout = () => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('user');
        sessionStorage.removeItem('postLoginRedirect');
        setUser(null);
        setIsAuthenticated(false);
    };

    // Merge partial fields into user state and localStorage (keeps the rest intact)
    const updateUser = (fields) => {
        setUser(prev => {
            const updated = { ...prev, ...fields };
            localStorage.setItem('user', JSON.stringify(updated));
            return updated;
        });
    };

    /**
     * Publish pending post after user authentication
     * @returns {Promise<{success: boolean, postId?: number, error?: string}>}
     */
    const publishPendingPost = async () => {
        try {
            const pendingPost = await getPendingPost();
            
            if (!pendingPost) {
                return { success: false, error: 'Нет сохраненного поста' };
            }

            const { postData, files } = pendingPost;
            const token = localStorage.getItem('access');

            if (!token) {
                return { success: false, error: 'Пользователь не авторизован' };
            }

            // Reconstruct FormData from saved post data
            const formData = new FormData();
            formData.append('title', postData.title);
            formData.append('country', postData.country);
            formData.append('region', postData.region || '');
            formData.append('number_of_people', postData.number_of_people || '');
            formData.append('number_of_days', postData.number_of_days || '');
            
            if (postData.trip_date) {
                const isoTripDate = new Date(postData.trip_date).toISOString();
                formData.append('trip_date', isoTripDate);
            }
            
            formData.append('budget.amount', postData.budget.amount);
            formData.append('budget.currency', postData.budget.currency || 'RUB');
            formData.append('is_price_per_person', postData.is_price_per_person ? 'true' : 'false');
            formData.append('main_transport', postData.main_transport || '');
            formData.append('score', postData.score);
            formData.append('description', postData.description);
            formData.append('departure_country', postData.departure_country);
            formData.append('departure_settlement', postData.departure_settlement?.id || '');

            // Add files
            files.forEach(file => {
                formData.append('attachments', file);
            });

            // Submit post
            const response = await api.post('/posts/', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                }
            });

            // Clear pending post after successful submission
            await clearPendingPost();

            return { 
                success: true, 
                postId: response.data?.id 
            };
        } catch (error) {
            console.error('Ошибка при публикации сохраненного поста:', error);
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Не удалось опубликовать пост. Попробуйте ещё раз.' 
            };
        }
    };

    const publishPendingTrip = async () => {
        try {
            const pendingTrip = await getPendingTrip();

            if (!pendingTrip) {
                return { success: false, error: 'Нет сохраненной поездки' };
            }

            const { tripData, imageFile } = pendingTrip;
            const token = localStorage.getItem('access');

            if (!token) {
                return { success: false, error: 'Пользователь не авторизован' };
            }

            const formDataPayload = new FormData();
            formDataPayload.append('title', tripData.title);
            formDataPayload.append('description', tripData.description);
            formDataPayload.append('departure_country', parseInt(tripData.departureCountry));
            formDataPayload.append('departure_settlement', tripData.departureSettlement?.id || '');
            formDataPayload.append('destination_country', parseInt(tripData.destinationCountry));
            formDataPayload.append('destination_settlement', tripData.destinationSettlement?.id || '');
            formDataPayload.append('trip_date', tripData.trip_date);
            formDataPayload.append('people_needed', tripData.people_needed);

            if (imageFile) {
                formDataPayload.append('image', imageFile);
            }

            const response = await companionsApi.createTrip(formDataPayload);
            await clearPendingTrip();

            return { success: true, tripId: response.data?.id };
        } catch (error) {
            console.error('Ошибка при публикации сохраненной поездки:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Не удалось опубликовать поездку. Попробуйте ещё раз.',
            };
        }
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            login,
            logout,
            setIsAuthenticated,
            updateUser,
            publishPendingPost,
            publishPendingTrip
        }}>
            {children}
        </AuthContext.Provider>
    );
};

